import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import { Label } from "@/components/ui/label/Label";
import { IPIC } from "@/models/management/PIC";
import { IManagedRoom } from "@/models/management/Room";
import { Trash } from "lucide-react";

type Props = {
  isOpen: boolean;
  room: IManagedRoom | null;
  availablePICs: IPIC[];
  onClose: () => void;
  onAssignPIC: (userId: number) => Promise<void>;
  onRemovePIC: (userId: number) => Promise<void>;
};

export function RoomDetailDialog({ isOpen, room, availablePICs, onClose, onAssignPIC, onRemovePIC }: Props) {
  const [selectedPicId, setSelectedPicId] = useState("");
  const [activeAction, setActiveAction] = useState<"assigning" | "removing" | null>(null);
  const isSubmitting = activeAction !== null;

  useEffect(() => {
    if (!isOpen) {
      setSelectedPicId("");
      setActiveAction(null);
    }
  }, [isOpen]);

  const assignablePICs = useMemo(() => {
    if (!room) return [];
    const assignedIds = new Set(room.room_pics.map((p) => p.user.id));
    return availablePICs.filter((p) => !assignedIds.has(p.id));
  }, [availablePICs, room]);

  const handleAssign = async () => {
    if (!selectedPicId || room?.is_inactive || isSubmitting) return;
    setActiveAction("assigning");
    try {
      await onAssignPIC(Number(selectedPicId));
      setSelectedPicId("");
    } finally {
      setActiveAction(null);
    }
  };

  const handleRemove = async (userId: number) => {
    if (!room || isSubmitting) return;
    setActiveAction("removing");
    try {
      await onRemovePIC(userId);
    } finally {
      setActiveAction(null);
    }
  };

  if (!room) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="max-w-3xl border-border bg-card text-foreground">
        <LoadingIndicator loading={isSubmitting} />
        <DialogHeader>
          <DialogTitle className="text-xl font-lsemibold">Room Detail</DialogTitle>
          <DialogDescription>View Room Details and manage assigned PICs.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid gap-3 md:grid-cols-2">
            {[
              { label: "Room ID", value: room.id },
              { label: "Room Code", value: room.room_code },
              { label: "Status", value: room.is_inactive ? "Inactive" : "Active" },
              { label: "Location", value: room.room_location?.location || "-" },
              { label: "Capacity", value: room.room_capacity },
              { label: "Description", value: room.room_description || "-" },
            ].map((item) => (
              <div key={item.label} className="p-3 rounded-lg border border-border bg-background/50">
                <div className="text-[10px] font-lsemibold uppercase text-muted-foreground">{item.label}</div>
                <div className="text-sm font-lsemibold mt-1">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="p-5 rounded-lg border border-border bg-secondary/5">
            <div className="text-sm font-lsemibold mb-4">Assigned PICs</div>
            <div className="border rounded-md bg-card overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-secondary/30 border-b border-border">
                  <tr>
                    <th className="px-4 py-2 w-[30%]">Initial</th>
                    <th className="px-4 py-2 w-[55%]">Name</th>
                    {!room.is_inactive && <th className="px-4 py-2 text-center w-[15%]">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {room.room_pics.length > 0 ? room.room_pics.map((pic) => (
                    <tr key={pic.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-lsemibold text-blue-600">{pic.user.initial}</td>
                      <td className="px-4 py-2 truncate">{pic.user.name}</td>
                      {!room.is_inactive && (
                        <td className="px-4 py-2 text-center">
                          <button
                            disabled={isSubmitting}
                            onClick={() => handleRemove(pic.user.id)}
                            className="p-1.5 hover:text-rose-600 disabled:opacity-30"
                          >
                            <Trash size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr><td colSpan={3} className="px-4 py-6 text-center italic text-muted-foreground">No PICs assigned.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-5 pt-5 border-t border-border grid gap-3 md:grid-cols-[1fr_auto] items-end">
              <div className="space-y-1.5">
                <Label className="text-xs ml-1">Assign New PIC</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                  value={selectedPicId}
                  onChange={(e) => setSelectedPicId(e.target.value)}
                  disabled={isSubmitting || room.is_inactive || assignablePICs.length === 0}
                >
                  <option value="" disabled>{room.is_inactive ? "Room Inactive" : "Select PIC..."}</option>
                  {assignablePICs.map((p) => <option key={p.id} value={p.id}>{p.initial} - {p.name}</option>)}
                </select>
              </div>
              <Button
                variant="blue"
                className="h-10 px-6 font-lsemibold"
                disabled={!selectedPicId || isSubmitting || room.is_inactive}
                onClick={handleAssign}
              >
                {activeAction === "assigning" ? "Assigning..." : activeAction === "removing" ? "Removing..." : "Assign PIC"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
